// @flow
const GEOCODE_API: string = "/api/geocode/geocode";

export type HereTimezone = {
  name: string,
  utcOffset?: string,
  lat?: number,
  lng?: number,
};

export type TimezoneLookupAddress = {
  address1?: ?string,
  city?: ?string,
  state?: ?string,
  postalCode?: ?string,
};

export const formatAddressQuery = ({
  address1,
  city,
  state,
  postalCode,
}: TimezoneLookupAddress): string =>
  [address1, city, state, postalCode]
    .map((part: ?string): string => (part || "").trim())
    .filter(Boolean)
    .join(", ");

// Resolve an address to an IANA zone without applying a fallback.
export const lookupTimezone = async (
  address: TimezoneLookupAddress
): Promise<HereTimezone> => {
  const query = formatAddressQuery(address);

  if (!query) {
    throw new Error("lookupTimezone requires the location's address.");
  }

  const url = `${GEOCODE_API}?q=${encodeURIComponent(query)}&show=tz`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Time zone lookup failed (${response.status}). Choose the time zone manually.`
    );
  }

  const json = await response.json();
  const item = json?.items?.[0];
  const timeZone = item?.timeZone;

  if (!timeZone?.name) {
    throw new Error(
      "HERE returned no time zone for this address. Choose the time zone manually."
    );
  }

  return {
    name: timeZone.name,
    utcOffset: timeZone.utcOffset,
    lat: item.position?.lat,
    lng: item.position?.lng,
  };
};
